import { RootState } from '../../../app/store';
import { cartActions } from '../../../reducers/cartSlice';
import { AgentMode } from '../agentMode/agentMode.constants';
import { selectIsShadowModeEnabled } from '../config/config.selector';
import { CartScope, DRAFT_CART_SCOPE, MAIN_CART_SCOPE } from './cartScope';
import { draftCartActions } from './draftCart.slice';

/**
 * HITL-1617 — the ONE place that answers "which cart does this write go into?".
 *
 * ## Why it is not keyed on the mode
 *
 * The plan's answer was "the mode decides: `Shadow` → `draftCart`, `Intercept` →
 * `cartSlice`". That is not sufficient, and it is the thing this story had to get
 * right.
 *
 * `menuSlice.ts:1446` — inside `addHypothesisItemToCart` — calls the same
 * `addItemToCart` thunk the agent's surfaces call. That is the AI's own replay,
 * and it runs in `Shadow`, on every changed hypothesis frame. Route on the mode
 * alone and the AI's rebuilt items land in the agent's draft cart: the agent
 * watches their own work fill up with the AI's, and `cartSlice` — the cart that
 * actually becomes the order — quietly stops being rebuilt.
 *
 * So the discriminator is the cart holding the item being edited, which is a
 * property of the WRITE and not of the mode. `dialogSlice.selectedItemCartScope`
 * carries it, and it can only say `draft` once the agent has clicked a My Cart
 * line — which can only happen in `Shadow` and `Intercept`, the two modes where
 * My Cart is on screen at all.
 *
 * ## Why every existing call site is safe without being edited
 *
 * `selectedItemCartScope` defaults to `main` and every
 * `selectItemAndResetNavigation` payload that omits the new field resets it to
 * `main`. The AI's replay is covered twice over: it omits the field, and
 * `selectShouldAIDriveItemSelection` (HITL-1610) already stops it moving the
 * selection at all outside `Supervise`. A write whose origin never learned about
 * scopes therefore lands on the main cart, which is exactly where it landed
 * before this story.
 *
 * HITL-1618 extends this to `menuSlice.addItemToCart`, which needs one more
 * rule on top — `addedBy === AI` forces `main` regardless — because that thunk
 * is the one place the AI's replay and the agent's omnibar share a code path.
 */
export const selectWriteCartScope = (state: RootState): CartScope =>
  selectIsShadowModeEnabled(state)
    ? state.dialog.selectedItemCartScope
    : MAIN_CART_SCOPE;

/**
 * HITL-1619 — where a BRAND NEW item goes.
 *
 * `selectWriteCartScope` above answers "which cart holds the item being
 * edited?", which is a question about an item that already exists. An omnibar
 * add has no such item yet, so it needs the other question answered: **which
 * cart is the agent's workspace right now?**
 *
 * - `Shadow` — the draft. This is the whole of the mode: the agent builds their
 *   own cart in parallel while the AI drives, and the omnibar is how a cart gets
 *   built. Copying a line from the AI is a shortcut for when the AI happened to
 *   get one right, never the way in.
 * - `Intercept` — the order. The agent owns it; there is nothing to shadow.
 * - `Supervise` — never reached. The omnibar is disabled there and no My Cart
 *   exists, so there is nothing to add into.
 * - Flag off — the order, as today.
 *
 * The AI's own replay never comes through here: `menuSlice.addItemToCart` forces
 * `main` on `addedBy === AI` before consulting this.
 */
export const selectNewItemCartScope = (state: RootState): CartScope =>
  selectIsShadowModeEnabled(state) && state.agentMode?.mode === AgentMode.shadow
    ? DRAFT_CART_SCOPE
    : MAIN_CART_SCOPE;

/**
 * A union rather than a hand-written interface, so the callable surface is
 * exactly the reducers BOTH slices have. `draftCart` holds only the eight shared
 * item reducers (`cartSlice.itemReducers`) plus `applyOptionReplacement`, so
 * reaching for anything order-shaped — `clearCart`, the loyalty reducers, the
 * POS cart — is a compile error here rather than a runtime surprise on a cart
 * that can never send an order.
 */
export type ScopedCartActions = typeof cartActions | typeof draftCartActions;

export const cartActionsForScope = (scope: CartScope): ScopedCartActions =>
  scope === DRAFT_CART_SCOPE ? draftCartActions : cartActions;

/**
 * The items of whichever cart the scope names. Thunks that resolve a cart item
 * by id must read through this: `getState().cart.cartItems[id]` finds a
 * DIFFERENT item when the scope is `draft`, because both slices number
 * `cartItemId` from 1.
 */
export const cartItemsForScope = (state: RootState, scope: CartScope) =>
  scope === DRAFT_CART_SCOPE ? state.draftCart.cartItems : state.cart.cartItems;

export const cartStateForScope = (state: RootState, scope: CartScope) =>
  scope === DRAFT_CART_SCOPE ? state.draftCart : state.cart;
